import React from "react";
import {
  clearWarning,
  getWarningSince,
  onActivityChange,
} from "../../state/utils/activity";
import { onSessionEnded } from "../../state/utils/session-ended";

interface IdleWarningDialogProps {
  /** How long the countdown runs once the warning latches. */
  graceMs: number;
  /** "Stay signed in" — refreshes the token. Resolves when done. */
  onStaySignedIn: () => Promise<void> | void;
  /** "Sign out now" — takes the same path as the countdown running out. */
  onSignOut: () => void;
}

/**
 * The pre-sign-out warning.
 *
 * A sibling of the routes inside <Router> holding its own state, so its
 * one-second tick re-renders this component alone. Putting the countdown in
 * AppContent would remount the entire Dashboard every second (see
 * activity.ts for why).
 *
 * The countdown is derived from an absolute deadline each tick rather than
 * decremented, so clock drift and background-tab throttling cannot make it
 * wrong — a throttled tab simply shows the right number late.
 */
export const IdleWarningDialog: React.FC<IdleWarningDialogProps> = ({
  graceMs,
  onStaySignedIn,
  onSignOut,
}) => {
  const [warningSince, setWarningSince] = React.useState<number | null>(
    getWarningSince(),
  );
  const [remainingMs, setRemainingMs] = React.useState(graceMs);
  const [busy, setBusy] = React.useState(false);

  // The latch lives in activity.ts and may be set or cleared by another tab.
  React.useEffect(() => onActivityChange(() => setWarningSince(getWarningSince())), []);

  // If the session ends for any reason while this is up, get out of the way —
  // two dialogs at the same z-index would otherwise stack.
  React.useEffect(() => onSessionEnded(() => setWarningSince(null)), []);

  React.useEffect(() => {
    if (warningSince === null) return;
    const deadline = warningSince + graceMs;
    const tick = () => setRemainingMs(Math.max(0, deadline - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [warningSince, graceMs]);

  if (warningSince === null) return null;

  const seconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const label =
    minutes > 0
      ? `${minutes}:${String(seconds % 60).padStart(2, "0")}`
      : `${seconds}s`;

  const stay = async () => {
    if (busy) return;
    setBusy(true);
    // Clear the latch synchronously, before any await: a slow refresh would
    // otherwise let the next heartbeat sign the user out mid-click.
    clearWarning();
    try {
      await onStaySignedIn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="session-ended-backdrop" role="presentation">
      <div
        className="session-ended-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-warning-title"
      >
        <div className="session-ended-title" id="idle-warning-title">
          Still there?
        </div>
        <div className="session-ended-message">
          You will be signed out in{" "}
          {/* aria-live off: a one-second region would be read on every tick. */}
          <span className="idle-warning-countdown" role="timer" aria-live="off">
            {label}
          </span>{" "}
          because of inactivity.
        </div>
        <div className="session-dialog-actions">
          <button
            className="session-ended-button"
            onClick={stay}
            disabled={busy}
            autoFocus
          >
            {busy ? "…" : "STAY SIGNED IN"}
          </button>
          <button
            className="session-ended-button secondary"
            onClick={onSignOut}
            disabled={busy}
          >
            SIGN OUT NOW
          </button>
        </div>
      </div>
    </div>
  );
};
